// 1. React
import React, { useState, useEffect, useRef } from 'react';

// 2. Third-party
import { Modal, Steps, Button, Form, Tag, Empty, Space, Input, Rate, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import FormBuilder from 'antd-form-builder';

// 3. Constants
import { getLibraryFormTypeLabel, LIBRARY_FORM_TYPE_COLORS } from '@iso/constants/libraryFormTypes';
import { normalizeFieldWidget, isFieldVisible, withRowDividers } from '@iso/constants/libraryFieldWidgets';
// Side-effect: registers the shared string widgets ONCE (see schemaFormWidgets.js).
// signature / upload_file are placeholders here — the read-only preview never
// captures input; the interactive runtime overrides them with real components.
import '@iso/lib/helpers/schemaFormWidgets';

const { Step } = Steps;

// Read-only multi-step preview of a library form's schema_json (STG / SSM-02 view half).
const PreviewFormModal = ({ visible, form, onCancel }) => {
  const [previewForm] = Form.useForm();
  const [current, setCurrent] = useState(0);
  // Top of the preview body, for the step-change scroll below.
  const topRef = useRef(null);
  const hasRenderedRef = useRef(false);
  const [hiddenIds, setHiddenIds] = useState([]);
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    setCurrent(0);
    setHiddenIds([]);
    setFormValues({});
    previewForm.resetFields();
  }, [visible, form, previewForm]);

  const steps = form && form.schema_json && Array.isArray(form.schema_json.steps) ? form.schema_json.steps : [];

  // Conditional step visibility: a `switch` field carrying `hiddenId` hides the
  // step whose `id` matches when the switch is ON.
  const hideRules = [];
  steps.forEach((step) => {
    ((step.formMeta && step.formMeta.fields) || []).forEach((field) => {
      if (field.widget === 'switch' && field.hiddenId) {
        hideRules.push({ key: field.key, hiddenId: field.hiddenId });
      }
    });
  });

  const recomputeHidden = () => {
    const ids = [];
    hideRules.forEach((rule) => {
      if (previewForm.getFieldValue(rule.key)) {
        ids.push(rule.hiddenId);
      }
    });
    setHiddenIds(ids);
    setFormValues(previewForm.getFieldsValue(true));
  };

  // Put the top of the preview back in view when the step changes — the same
  // behaviour SchemaFormRenderer has. A long step otherwise leaves the reader
  // at the bottom of the modal, and Next swaps the questions out underneath
  // them, landing somewhere in the middle of the new step.
  //
  // The anchor sits INSIDE the modal body, so the browser scrolls the modal's
  // own scroll container rather than the page behind it.
  useEffect(() => {
    // Not on open — a preview appearing should not jump.
    if (!hasRenderedRef.current) {
      hasRenderedRef.current = true;
      return;
    }
    const el = topRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') {
      return;
    }
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [current]);

  // Closing and reopening the preview should start at the top again rather
  // than treating the next step change as the "first" one.
  useEffect(() => {
    if (!visible) {
      hasRenderedRef.current = false;
    }
  }, [visible]);

  const visibleSteps = steps.filter((step) => !(step.id && hiddenIds.indexOf(step.id) !== -1));
  const activeIndex = visibleSteps.length ? Math.min(current, visibleSteps.length - 1) : 0;
  const activeStep = visibleSteps[activeIndex];
  // Render-time meta: normalize legacy radio/checkbox-with-options to group
  // widgets; `formItemLayout: null` lets the vertical Form stack labels at full
  // column width; `gutter` puts a gap between columns.
  const activeMeta = activeStep && activeStep.formMeta
    ? {
        ...activeStep.formMeta,
        formItemLayout: null,
        gutter: 32,
        fields: withRowDividers(
          (activeStep.formMeta.fields || [])
            .filter((f) => isFieldVisible(f, formValues))
            .map(normalizeFieldWidget),
          activeStep.formMeta.columns
        ),
      }
    : null;

  const title = (
    <Space>
      {form ? form.name : 'Preview'}
      {form && form.type ? (
        <Tag color={LIBRARY_FORM_TYPE_COLORS[form.type]}>{getLibraryFormTypeLabel(form.type)}</Tag>
      ) : null}
    </Space>
  );

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={onCancel}
      width={1200}
      footer={[
        <Button key="close" onClick={onCancel}>
          Close
        </Button>,
      ]}
    >
      {/* Scroll anchor — see the step-change effect above. */}
      <div ref={topRef} aria-hidden="true" style={{ scrollMarginTop: 8 }} />
      {visibleSteps.length === 0 ? (
        <Empty description="No preview available" />
      ) : (
        <>
          {visibleSteps.length > 1 && (
            <Steps current={activeIndex} size="small" style={{ marginBottom: 16 }}>
              {visibleSteps.map((step, index) => (
                <Step key={step.id || index} title={step.title || `Step ${index + 1}`} />
              ))}
            </Steps>
          )}

          <Form form={previewForm} layout="vertical" className="preview-form-area" onValuesChange={recomputeHidden}>
            {activeMeta ? (
              <FormBuilder meta={activeMeta} form={previewForm} />
            ) : (
              <Empty description="Empty step" />
            )}
          </Form>

          {visibleSteps.length > 1 && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
              <Button disabled={activeIndex === 0} onClick={() => setCurrent(activeIndex - 1)}>
                Previous
              </Button>
              <Button disabled={activeIndex === visibleSteps.length - 1} onClick={() => setCurrent(activeIndex + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default PreviewFormModal;
